// ── Pricing Tier Definitions ──
// Minimum (near-cost floor) and Groups A–D, plus Website (public retail) and Distributor.
// Labels are internal — never shown to clients/providers.

export const PRICING_TIERS = {
  min: { label: "Minimum", shortLabel: "Min", description: "Floor", color: "#14b8a6" },
  A: { label: "Group A", shortLabel: "A", description: "Preferred Partner", color: "#34c759" },
  B: { label: "Group B", shortLabel: "B", description: "Established Partner", color: "#007aff" },
  C: { label: "Group C", shortLabel: "C", description: "Standard Partner", color: "#ff9500" },
  D: { label: "Group D", shortLabel: "D", description: "New Partner", color: "#ff3b30" },
  W: { label: "Website", shortLabel: "W", description: "Retail", color: "#6b7280" },
  distrib: { label: "Distributor", shortLabel: "Dist", description: "Cost Plus", color: "#7c3aed" },
};

export const TIER_KEYS = ["min", "A", "B", "C", "D", "W"];

// All tiers including distrib — for dropdowns that offer all pricing levels.
// Order is cheapest → most expensive: min (floor) < distrib (cost-plus) < A … < W.
export const ALL_TIER_KEYS = ["min", "distrib", "A", "B", "C", "D", "W"];

// ── Rank-based helpers for ceiling enforcement ──
// Lower rank = better tier. min=0, A=1, ..., W=5.
// "distrib" is internal cost-plus only and intentionally NOT in the rank table
// — it must never participate in signup-time ceiling comparisons.

export function rankOf(tier) {
  // Defensive case-insensitive parity with the BE version
  // (lucasai-backend/lib/pricingTierRank.js:rankOf). Lowercase or other-cased
  // strings shouldn't reach here under normal writes (both TIER_KEYS.indexOf
  // validators are case-sensitive on the way in), but a stale-migration value
  // or direct-write would otherwise trigger the permissive fall-through in
  // getAllowedTiers + canGrant. Comparing case-insensitively on both sides
  // (rather than assuming TIER_KEYS entries are uppercase) also supports the
  // lowercase "min"/"distrib" keys.
  if (tier == null || typeof tier !== "string") return null;
  const lc = tier.toLowerCase();
  const idx = TIER_KEYS.findIndex((k) => k.toLowerCase() === lc);
  return idx === -1 ? null : idx;
}

export function compareTiers(a, b) {
  const ra = rankOf(a);
  const rb = rankOf(b);
  if (ra === null || rb === null) return 0;
  return ra - rb;
}

// Returns the tier keys the caller is allowed to grant when onboarding.
// - Supervisors bypass the ceiling and see ALL tiers including the internal
//   "distrib" (Cost Plus) tier — only supervisors/admins can grant distributor
//   pricing.
// - Callers with no/invalid tier fall through to the standard list, without
//   distrib (legacy safety: don't let a missing-tier session quietly hand out
//   cost-plus pricing).
// - Otherwise, the caller can only grant tiers STRICTLY worse than their own
//   (e.g. an A-tier caller sees [B, C, D] — they cannot clone their own
//   ceiling onto someone else). This prevents tier-laundering (rep A at C
//   spawning rep B at C, both competing at the same wholesale level). Distrib
//   never appears in the non-supervisor path because it's not rank-comparable.
//
// NOTE: The BE rule at lucasai-backend/lib/pricingTierRank.js:29 currently
// allows ">=" (caller's tier or worse). Until the BE is updated to match
// (">" strict worse), a devtools-savvy caller could still write a same-tier
// grant by bypassing the picker. The UX surface honors the rule fully.
// W (Website/Retail) IS grantable. It means the provider buys at the same
// price a retail patient pays — no wholesale discount. That was banned
// outright on 2026-07-07 on the theory that a zero-margin account is always a
// mistake; reversed 2026-07-22, because signing a provider who is willing to
// pay public pricing is a legitimate sales motion, and the ban left D-tier
// reps (the worst wholesale tier) with NO grantable tier at all — nothing is
// strictly worse than D once W is removed — so they could not onboard anyone.
// W now obeys the same strictly-worse ceiling rule as every other tier: a
// D-tier caller may grant only W; a W-tier caller may still grant nothing.
// The BE agrees without any change (backend/lib/pricingTierRank.js:canGrant is
// strictly-worse rank and already carries "W" in its TIER_KEYS).
//
// "min" remains excluded from the standard grantable set — same rationale as
// "distrib": it's a near-cost floor price, supervisor/manufacturer-granted
// only. An untiered (null-caller) or rank-based rep must never be able to
// hand a provider the floor price. It's re-added explicitly in the
// supervisor branch below, alongside distrib.
const GRANTABLE_TIER_KEYS = TIER_KEYS.filter((k) => k !== "min"); // ["A","B","C","D","W"]

export function getAllowedTiers(callerTier, { isSupervisor } = {}) {
  if (isSupervisor) return ["min", ...GRANTABLE_TIER_KEYS, "distrib"];
  const r = rankOf(callerTier);
  if (r === null) return GRANTABLE_TIER_KEYS;
  // GRANTABLE_TIER_KEYS no longer index-aligns with TIER_KEYS (min was
  // filtered out), so slice-by-rank-index would skip/misalign entries.
  // Filter by rank comparison instead.
  return GRANTABLE_TIER_KEYS.filter((k) => rankOf(k) > r);
}

// ── Picker option labels ──
// Single source of truth for how one tier renders inside a <select>. Four
// pickers (OnboardingView ×2, UserEdit, ProviderInfoDrawer,
// ModalSendStackBuilderLink) previously each carried their own
// `key === "distrib" ? ... : \`Tier ${key}\`` ternary, which meant a new
// non-lettered key rendered as "Tier distrib" / "Tier min" in whichever copy
// hadn't been updated. Labels stay untranslated English here, matching the
// literals they replace — these are internal tier names, not user prose.
export function tierOptionLabel(key) {
  if (!key || !PRICING_TIERS[key]) return key || "";
  if (key === "distrib") return "Distributor — Cost Plus";
  // W is the patient-retail lens, not a wholesale tier — never render it as
  // "Tier W" alongside A–D, or reps read it as one more discount level.
  if (key === "W") return "Retail — Public/Website Pricing";
  if (key === "min") return "Minimum — Floor";
  return `Tier ${key} — ${PRICING_TIERS[key].description}`;
}

// ── Rep cost-basis bump for below-tier provider accounts ──
// The rep's EFFECTIVE cost-basis tier for one provider account. When a
// manufacturer/supervisor has placed the provider on a tier at least as good as
// the rep's own, bump the rep's basis to exactly one tier better than the
// provider — so the rep always keeps a one-tier spread on that account.
// Otherwise the rep's own tier stands. On the full ladder this is
// min(repTier, providerTier − 1); "one better than A" is the internal
// Distributor (Cost-Plus) tier. A better tier is always cheaper, so the bump can
// only raise or hold a rep's commission, never lower it. Unknown / unrankable
// inputs pass the rep's own tier through unchanged (no bump). Mirrors the
// backend rule that must live in lib/repCommission.js (see backend guide
// 2026-07-18-peptide-rep-tier-bump.md).
export function effectiveRepBasisTier(repTier, providerTier) {
  // Case-insensitive lookup (mirrors rankOf): a stale/lowercased stored tier
  // must still resolve, not silently disable the bump. Returns the ORIGINAL
  // repTier (case preserved) on an unknown input.
  const idxOf = (t) => (typeof t === "string" ? ALL_TIER_KEYS.findIndex((k) => k.toLowerCase() === t.toLowerCase()) : -1);
  const repIdx = idxOf(repTier);
  const provIdx = idxOf(providerTier);
  if (repIdx === -1 || provIdx === -1) return repTier; // unknown tier → no bump
  const oneAboveProvider = Math.max(0, provIdx - 1); // clamp at distrib (top of ladder)
  return ALL_TIER_KEYS[Math.min(repIdx, oneAboveProvider)]; // better = lower index
}
